import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const DataImportManager = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadImportHistory();
  }, []);

  const loadImportHistory = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/data-import/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImportHistory(response.data);
    } catch (error) {
      console.error('Load history error:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      handlePreview(selectedFile);
    }
  };

  const handlePreview = async (fileToPreview) => {
    try {
      const formData = new FormData();
      formData.append('file', fileToPreview);

      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_URL}/data-import/preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreview(response.data);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview file');
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_system', 'csv_import');

      // Always use smart/flexible import
      const endpoint = '/data-import/clients-flexible';

      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_URL}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const message = `Import completed! ${response.data.successful} clients imported, ${response.data.failed} failed`;

      toast.success(message);

      // Reset form
      setFile(null);
      setPreview(null);
      loadImportHistory();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/data-import/export/clients`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Clients exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export clients');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Upload className="text-primary-600" size={24} />
          <div>
            <h2 className="text-2xl font-bold">Client Data Import/Export</h2>
            <p className="text-gray-600">Import clients from other systems or export your data</p>
          </div>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="btn btn-secondary"
        >
          {showHistory ? 'Hide' : 'Show'} History
        </button>
      </div>

      {/* Import Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Import */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center space-x-2">
            <Upload size={20} />
            <span>Import Clients</span>
          </h3>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>🎯 Smart Import:</strong> Automatically maps CSV columns to your database fields. 
                Unmapped data is saved to Notes for manual review. Works with any CSV structure!
              </p>
            </div>

            <div>
              <label className="label">Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-50 file:text-primary-700
                  hover:file:bg-primary-100"
              />
            </div>

            {file && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Selected:</strong> {file.name}
                </p>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="btn btn-primary w-full flex items-center justify-center space-x-2"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload size={18} />
                  <span>Import Clients</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Export */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center space-x-2">
            <Download size={20} />
            <span>Export Clients</span>
          </h3>

          <p className="text-gray-600 mb-4">
            Download all your client data as a CSV file. This can be used for backups or importing into other systems.
          </p>

          <button
            onClick={handleExport}
            className="btn btn-secondary w-full flex items-center justify-center space-x-2"
          >
            <Download size={18} />
            <span>Export All Clients</span>
          </button>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The exported CSV will include all client information and can be re-imported later.
            </p>
          </div>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="card bg-gray-50 mb-6">
          <h3 className="font-bold text-lg mb-4">File Preview (First 10 rows)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  {preview.headers.map((header, index) => (
                    <th key={index} className="text-left py-2 px-3 font-semibold">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-gray-200">
                    {preview.headers.map((header, colIndex) => (
                      <td key={colIndex} className="py-2 px-3">
                        {row[header]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Showing {preview.preview.length} of {preview.total_preview_rows} preview rows
          </p>
        </div>
      )}

      {/* Import History */}
      {showHistory && (
        <div className="card bg-gray-50">
          <h3 className="font-bold text-lg mb-4">Import History</h3>
          <div className="space-y-3">
            {importHistory.map((record) => (
              <div key={record.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText size={18} className="text-gray-600" />
                      <span className="font-semibold">{record.file_name}</span>
                      {record.status === 'completed' && (
                        <CheckCircle size={18} className="text-green-600" />
                      )}
                      {record.status === 'failed' && (
                        <XCircle size={18} className="text-red-600" />
                      )}
                      {record.status === 'processing' && (
                        <AlertCircle size={18} className="text-yellow-600" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Source:</span>
                        <span className="ml-2 font-medium">{record.source_system}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total:</span>
                        <span className="ml-2 font-medium">{record.total_records}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Success:</span>
                        <span className="ml-2 font-medium text-green-600">
                          {record.successful_records}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Failed:</span>
                        <span className="ml-2 font-medium text-red-600">
                          {record.failed_records}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Imported by {record.imported_by_name} on{' '}
                      {new Date(record.import_date).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">CSV Format Requirements:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• File must be in CSV format (.csv)</li>
          <li>• First row should contain column headers</li>
          <li>• <strong>Smart Import:</strong> Automatically detects and maps columns from any CSV structure</li>
          <li>• <strong>Client fields:</strong> name, first name, last name, email, phone, address, city, state, zip, notes</li>
          <li>• <strong>Subject fields:</strong> subject/pet name, breed, size, color, age, weight, gender, medical notes, etc.</li>
          <li>• Email or phone is required for each client</li>
          <li>• Column order doesn't matter — the system matches by column name</li>
          <li>• Column names are flexible: "First Name", "first_name", "firstname", "fname" all work</li>
          <li>• Existing clients (matched by email) will be updated, new ones will be created</li>
          <li>• Unrecognized columns are preserved in client notes for manual review</li>
        </ul>
        
        <div className="mt-3 p-3 bg-white rounded border border-blue-300">
          <p className="text-sm font-semibold text-blue-900 mb-1">Example CSV (Clients Only):</p>
          <code className="text-xs text-gray-700">
            First Name,Last Name,Email,Phone,Address,City,State,ZIP<br/>
            John,Smith,john@email.com,555-1234,123 Main St,Austin,TX,78701
          </code>
        </div>
        
        <div className="mt-2 p-3 bg-white rounded border border-blue-300">
          <p className="text-sm font-semibold text-blue-900 mb-1">Example CSV (Clients + Subjects):</p>
          <code className="text-xs text-gray-700">
            Client Name,Email,Phone,Pet Name,Breed,Size,Notes<br/>
            John Smith,john@email.com,555-1234,Buddy,Golden Retriever,Large,Regular customer
          </code>
        </div>
      </div>
    </div>
  );
};

export default DataImportManager;
